import { apiClient } from './api';
import { storage, STORAGE_KEYS } from '@utils/storage';
import type { User, AuthTokens } from '@/types';

type SocialProvider = 'google' | 'facebook' | 'apple';

export const authService = {
  async login(email: string, password: string): Promise<{ user: User; tokens: AuthTokens }> {
    const guestId = storage.get<string>(STORAGE_KEYS.GUEST_ID);

    const response = await apiClient.post<{
      access_token: string;
      refresh_token: string;
      user: {
        id: string;
        email: string;
        name: string | null;
        surname: string | null;
        picture: string | null;
        role: string;
        isEmailVerified: boolean;
        mustChangePassword?: boolean;
        createdAt: string;
      };
    }>('/auth/login', { email, password, ...(guestId && { guestId }) });

    const data = response.data.data;

    const tokens: AuthTokens = {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    };

    const user: User = {
      id: data.user.id,
      email: data.user.email,
      name: data.user.name || undefined,
      surname: data.user.surname || undefined,
      picture: data.user.picture || null,
      role: data.user.role as User['role'],
      isVerified: data.user.isEmailVerified,
      mustChangePassword: data.user.mustChangePassword || false,
      createdAt: data.user.createdAt,
      phone: null,
    };

    return { user, tokens };
  },

  async register(email: string, password: string, name: string, surname: string): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>('/auth/register', {
      email,
      password,
      name,
      surname,
    });
    return response.data.data;
  },

  async retryPendingRegistration(
    email: string,
    password: string,
    name: string,
    surname: string,
  ): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>('/auth/retry-pending-registration', {
      email,
      password,
      name,
      surname,
    });
    return response.data.data;
  },

  async resendVerificationEmail(email: string): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>('/auth/resend-verification-email', {
      email,
    });
    return response.data.data;
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>('/auth/forgot-password', { email });
    return response.data.data;
  },

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>('/auth/reset-password', {
      token,
      newPassword,
    });
    return response.data.data;
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data.data;
  },

  async socialLogin(provider: SocialProvider, token: string): Promise<{ user: User; tokens: AuthTokens }> {
    const guestId = storage.get<string>(STORAGE_KEYS.GUEST_ID);
    const response = await apiClient.post<{
      access_token: string;
      refresh_token: string;
      user: {
        id: string;
        email: string;
        name: string | null;
        surname: string | null;
        picture: string | null;
        role: string;
        isEmailVerified: boolean;
        createdAt: string;
        phone?: string | null;
      };
    }>('/auth/social', { provider, token, ...(guestId && { guestId }) });
    
    const data = response.data.data as any;
    
    const user: User = {
      id: data.user.id,
      phone: data.user.phone ?? null,
      email: data.user.email,
      name: data.user.name || undefined,
      surname: data.user.surname || undefined,
      picture: data.user.picture || null,
      role: data.user.role as User['role'],
      isVerified: data.user.isEmailVerified,
      createdAt: data.user.createdAt,
    };
    
    const tokens: AuthTokens = {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    };
    
    return { user, tokens };
  },

  async refreshToken(refreshToken: string) {
    const response = await apiClient.post<AuthTokens>('/auth/refresh', {
      refresh_token: refreshToken,
    });
    return response.data.data;
  },

  async updateProfile(data: { phone?: string | null; email?: string }): Promise<User> {
    const response = await apiClient.patch<{
      id: string;
      phone: string | null;
      email: string;
      name: string | null;
      surname: string | null;
      picture: string | null;
      role: string;
      isEmailVerified: boolean;
      createdAt: string;
    }>('/auth/me', data);

    const d = response.data.data;
    return {
      id: d.id,
      phone: d.phone || null,
      email: d.email || undefined,
      name: d.name || undefined,
      surname: d.surname || undefined,
      picture: d.picture || null,
      role: d.role as User['role'],
      isVerified: d.isEmailVerified,
      createdAt: d.createdAt,
    };
  },

  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<{
      id: string;
      phone: string | null;
      email: string;
      name: string | null;
      surname: string | null;
      picture: string | null;
      role: string;
      isEmailVerified: boolean;
      createdAt: string;
    }>('/auth/me');
    
    const data = response.data.data;
    
    return {
      id: data.id,
      phone: data.phone || null,
      name: data.name || undefined,
      surname: data.surname || undefined,
      email: data.email || undefined,
      picture: data.picture || null,
      role: data.role as User['role'],
      isVerified: data.isEmailVerified,
      createdAt: data.createdAt,
    };
  },

  /**
   * Logout (invalidate refresh token)
   */
  async logout() {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  },
};
